// ============================================================
// THE KILL-TEAM BLOCK (plan modes-rework, 2026-10-27) - a kill-team as its
// members' sheets carry it: the Tactics item it plays from, a COPY of the
// Oath chosen from it (players never read the GM's library, so the abilities
// travel on the sheet), the support range and the GM's Cohesion adjustment.
//
// TEAM-level: every member carries the same block, and the Team tab and the
// Teams view rewrite them all. The LEADER is not in it: being the leader is a
// member's own `team.leads`.
//
// Everything read off a sheet goes through `readKillTeamBlock`.
//
// Pure. Zero React dependency.
// ============================================================
import type { Origin } from '../assets/types';
import { readOrigin } from '../assets/provenance';
import { oathAbilities, readAbilities, readOathHead, type SquadAbility, type Tactics } from './tactics';
import { int, nonZero, optional, text, type Raw } from './read';

/** A support range when none is set: the book's commonest. */
export const DEFAULT_RANGE_M = 30;
/** The support range's stepper, in metres. */
const RANGE_M = { min: 5, max: 200, step: 5 } as const;
/** The GM's Cohesion adjustment, either way. */
const ADJUSTMENT_LIMIT = 20;

/** The Oath as the sheet keeps it: its own fields and the abilities it unlocks, copied whole. */
export interface OathCopy {
  id: string;
  name: string;
  /** Added to the starting Cohesion (-10..10); absent for none. */
  cohesionBonus?: number;
  abilities: SquadAbility[];
  text?: string;
}

export interface KillTeamBlock {
  /** The Tactics item the Oath was chosen from, and the library version it was copied at. */
  tactics?: { name: string; origin?: Origin };
  /** None yet: the kill-team is set up but no brother can enter Squad Mode. */
  oath?: OathCopy;
  rangeM: number;
  /** Added to the starting Cohesion; absent for none. */
  adjustment?: number;
}

function readSource(raw: unknown): KillTeamBlock['tactics'] | null {
  const name = text((raw as Raw | null)?.name);
  return name ? { name, ...optional('origin', readOrigin((raw as Raw).origin)) } : null;
}

function readOathCopy(raw: unknown): OathCopy | null {
  const head = readOathHead(raw);
  return head ? { ...head, abilities: readAbilities((raw as Raw).abilities) } : null;
}

/**
 * A stored block, validated: null for what is not one at all; the range and the adjustment clamped. Read where a sheet
 * comes in (`normalizeCharacter`, the library's `readTeam`); past that, a sheet's block is trusted as typed.
 */
export function readKillTeamBlock(raw: unknown): KillTeamBlock | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Raw;
  return {
    ...optional('tactics', readSource(r.tactics)),
    ...optional('oath', readOathCopy(r.oath)),
    rangeM: int(r.rangeM, RANGE_M.min, RANGE_M.max) ?? DEFAULT_RANGE_M,
    ...optional('adjustment', nonZero(int(r.adjustment, -ADJUSTMENT_LIMIT, ADJUSTMENT_LIMIT))),
  };
}

/** The support range a step (+1 / -1) away, clamped. */
export const stepRange = (block: KillTeamBlock, step: number): KillTeamBlock => ({
  ...block,
  rangeM: Math.min(RANGE_M.max, Math.max(RANGE_M.min, block.rangeM + step * RANGE_M.step)),
});

/** The GM's adjustment one point either way, clamped; 0 is none (gotcha 2). */
export function stepAdjustment(block: KillTeamBlock, step: number): KillTeamBlock {
  const { adjustment = 0, ...rest } = block;
  const next = Math.min(ADJUSTMENT_LIMIT, Math.max(-ADJUSTMENT_LIMIT, adjustment + step));
  return next ? { ...rest, adjustment: next } : rest;
}

/** A kill-team switched on with nothing chosen yet. */
export const blankBlock = (): KillTeamBlock => ({ rangeM: DEFAULT_RANGE_M });

/** The sheet's copy of one of the item's Oaths; null for an Oath it does not hold. */
export function oathCopyOf(tactics: Tactics, oathId: string): OathCopy | null {
  const oath = tactics.oaths.find((o) => o.id === oathId);
  if (!oath) return null;
  return {
    id: oath.id,
    name: oath.name,
    // A zero bonus is no bonus, as the sheet reads it back: a block must compare the same before and after a save.
    ...optional('cohesionBonus', nonZero(oath.cohesionBonus ?? null)),
    abilities: oathAbilities(tactics, oath.id),
    ...optional('text', oath.text),
  };
}

/** The block with an Oath chosen from a Tactics item (its library version as the origin); its range and adjustment
 * kept. An Oath the item does not hold leaves the block as it was. */
export function withOath(block: KillTeamBlock, tactics: Tactics, oathId: string, origin: Origin | null): KillTeamBlock {
  const oath = oathCopyOf(tactics, oathId);
  if (!oath) return block;
  return { ...block, tactics: { name: tactics.name, ...optional('origin', origin) }, oath };
}
